import React, { useState, useContext } from "react";
import { Modal, Button, Popover, Descriptions, message } from "antd";
import {
  BookOutlined,
  PlayCircleOutlined,
  ClockCircleOutlined,
  UserOutlined,
  LinkedinOutlined,
  ArrowRightOutlined,
  ReadOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import useEnrolledCourses from "PlatForms/SelfLMS/Hooks/Courses/useEnrolledCourses";
import { selfLearnerService } from "PlatForms/SelfLMS/Services/Courses/CourseService";
import { ProfileContext } from "Common/hooks/context/ProfileContext";

const EnrolledCoursesCard = () => {
  const navigate = useNavigate();
  const { enrollments, loading, error } = useEnrolledCourses();

  const [startingCourse, setStartingCourse] = useState(null);
  const { isProfileComplete } = useContext(ProfileContext);
  const isBlocked = !isProfileComplete;

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedPriceData, setSelectedPriceData] = useState(null);

  const displayedCourses = enrollments.slice(0, 6);

  const openPriceModal = (e, course) => {
    e.stopPropagation();
    setSelectedPriceData(course);
    setIsModalVisible(true);
  };

  const formatTime = (timeString) => {
    if (!timeString || timeString === "00:00:00") return "Not specified";
    const [hours, minutes] = timeString.split(":");
    const h = parseInt(hours, 10);
    const m = parseInt(minutes, 10);
    if (h === 0 && m === 0) return "Not specified";
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  };

  const getAuthorName = (course) => {
    if (course?.courseAuthor?.name) return course.courseAuthor.name;
    if (course?.authorName) return course.authorName;
    return "Unknown author";
  };

  const getLinkedInUrl = (course) => {
    if (course?.courseAuthor?.linkedinUrl) return course.courseAuthor.linkedinUrl;
    if (course?.linkedinUrl) return course.linkedinUrl;
    if (course?.authorLinkedIn) return course.authorLinkedIn;
    return null;
  };

  const handleContinueLearning = async (e, enrollmentId) => {
    if (e) e.stopPropagation();
    if (isBlocked) {
      message.warning("Please complete your profile first");
      return;
    }
    try {
      setStartingCourse(enrollmentId);
      const response = await selfLearnerService.startCourseEnrollment(enrollmentId);
      if (response.isAssessmentPage) {
        navigate(
          `/selfLearner-dashboard/enrolled-courses/${enrollmentId}/modules/${response.moduleProgressId}/assessment`,
          {
            state: {
              moduleId: response.moduleId,
              assessmentId: response.assessmentPage.id,
              moduleProgressId: response.moduleProgressId,
              enrollmentId: enrollmentId,
              assessmentTitle: response.assessmentPage.title,
              assessmentData: response,
            },
          }
        );
      } else {
        const pageId =
          response.currentPageId || response.previousPageId || response.nextPageId || response.moduleId;
        navigate(
          `/selfLearner-dashboard/enrolled-courses/${enrollmentId}/modules/${response.moduleProgressId}/pages/${pageId}`,
          { state: { moduleId: response.moduleId } }
        );
      }
    } catch (err) {
      console.error("Error starting course:", err);
      message.error("Failed to start course. Please try again.");
    } finally {
      setStartingCourse(null);
    }
  };

  const openDetail = (enrollmentId) =>
    navigate(`/selfLearner-dashboard/enrolled-courses/courses/${enrollmentId}`);
  const handleExploreNewCourses = () => navigate("/selfLearner-dashboard/available-courses");
  const handleViewAllEnrolledCourses = () => navigate("/selfLearner-dashboard/enrolled-courses");

  const sectionHead = (
    <div className="sl-section__head">
      <h2 className="sl-section__title">
        My courses
        <small>Resume where you left off</small>
      </h2>
      <button type="button" className="sl-section__link" onClick={handleExploreNewCourses}>
        Explore new <ArrowRightOutlined />
      </button>
    </div>
  );

  if (loading) {
    return (
      <section className="sl-section">
        {sectionHead}
        <div className="sl-grid">
          {[0, 1, 2].map((i) => (
            <div className="sl-skel sl-skel--card" key={i} />
          ))}
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="sl-section">
        {sectionHead}
        <div className="sl-empty">
          <div className="sl-empty__icon">
            <ReadOutlined />
          </div>
          <h3 className="sl-empty__title">We could not load your courses</h3>
          <p className="sl-empty__text">Please refresh and try again.</p>
          <Button type="primary" onClick={() => window.location.reload()}>
            Refresh
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section className="sl-section">
      {sectionHead}

      {enrollments.length === 0 ? (
        <div className="sl-empty">
          <div className="sl-empty__icon">
            <BookOutlined />
          </div>
          <h3 className="sl-empty__title">No enrolled courses yet</h3>
          <p className="sl-empty__text">
            Browse the catalog and enroll in your first course to start learning.
          </p>
          <Button type="primary" onClick={handleExploreNewCourses}>
            Browse courses
          </Button>
        </div>
      ) : (
        <>
          <div className="sl-grid">
            {displayedCourses.map((enrollment) => {
              const course = enrollment.course || {};
              const pct = enrollment.isCompleted
                ? 100
                : Math.round(((enrollment.completedModuleCount || 0) / (enrollment.moduleCount || 1)) * 100);
              const authorName = getAuthorName(course);
              const linkedInUrl = getLinkedInUrl(course);
              const statusCls = enrollment.isCompleted
                ? "sl-status--done"
                : enrollment.isBlocked
                ? "sl-status--blocked"
                : "sl-status--progress";
              const statusText = enrollment.isCompleted
                ? "Completed"
                : enrollment.isBlocked
                ? "Blocked"
                : "In progress";
              const priceLabel = course.isPaid
                ? `${course.pricing?.currency === "USD" ? "$" : course.pricing?.currency || "$"}${course.pricing?.price || 0}`
                : "FREE";

              return (
                <div
                  className="sl-card"
                  key={enrollment.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openDetail(enrollment.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openDetail(enrollment.id);
                    }
                  }}
                >
                  <div className="sl-card__media">
                    <BookOutlined />
                    <span
                      className={`sl-badge ${course.isPaid ? "sl-badge--paid" : ""}`}
                      onClick={(e) => openPriceModal(e, course)}
                      style={{ cursor: "pointer" }}
                    >
                      {priceLabel}
                    </span>
                  </div>

                  <div className="sl-card__body">
                    <h3 className="sl-card__title">{course.title}</h3>

                    <Popover
                      trigger="click"
                      content={
                        <span>
                          {authorName}
                          {linkedInUrl && (
                            <a href={linkedInUrl} target="_blank" rel="noreferrer" style={{ marginLeft: 6 }}>
                              <LinkedinOutlined />
                            </a>
                          )}
                        </span>
                      }
                    >
                      <span className="sl-card__author" onClick={(e) => e.stopPropagation()}>
                        <UserOutlined /> {authorName}
                      </span>
                    </Popover>

                    <div className="sl-card__progress">
                      <div className="sl-card__progress-row">
                        <span className={`sl-status ${statusCls}`}>{statusText}</span>
                        <span>{pct}%</span>
                      </div>
                      <div className="sl-track">
                        <span
                          className={enrollment.isCompleted ? "is-done" : ""}
                          style={{ transform: `scaleX(${pct / 100})` }}
                        />
                      </div>
                      <div className="sl-card__progress-row">
                        <span>
                          {enrollment.completedModuleCount || 0}/{enrollment.moduleCount || 0} modules
                        </span>
                        <span>
                          <ClockCircleOutlined /> {formatTime(course.estimatedTime)}
                        </span>
                      </div>
                    </div>

                    <Button
                      type={enrollment.isCompleted || enrollment.isBlocked ? "default" : "primary"}
                      icon={<PlayCircleOutlined />}
                      block
                      loading={startingCourse === enrollment.id}
                      disabled={enrollment.isBlocked || isBlocked}
                      onClick={(e) => handleContinueLearning(e, enrollment.id)}
                    >
                      {enrollment.isCompleted
                        ? "Review course"
                        : enrollment.isBlocked
                        ? "Blocked"
                        : "Continue learning"}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 18, textAlign: "center" }}>
            <Button type="default" icon={<ArrowRightOutlined />} onClick={handleViewAllEnrolledCourses}>
              View all enrollments
            </Button>
          </div>
        </>
      )}

      <Modal
        title="Course price details"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
      >
        {selectedPriceData && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Course">{selectedPriceData.title}</Descriptions.Item>
            <Descriptions.Item label="Price">
              {selectedPriceData.isPaid
                ? `${selectedPriceData.pricing?.currency || "$"}${
                    selectedPriceData.pricing?.price?.toFixed?.(2) || "0.00"
                  }`
                : "FREE"}
            </Descriptions.Item>
            {selectedPriceData.isPaid && selectedPriceData.pricing?.accessDurationDays && (
              <Descriptions.Item label="Access duration">
                {selectedPriceData.pricing.accessDurationDays} days
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </section>
  );
};

export default EnrolledCoursesCard;
